import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  AysNrwServiceGroupId,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  AYS_NRW_SERVICE_GROUPS_DISPLAY_NAMES
} from '@bindoc-projects/virtual-clinic-domain-lib';
import type { FilterConditionPlugin } from '../filter';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_NRW_SERVICE_GROUP_CONDITION_ID = 'nrwServiceGroup';
export const FilterConditionNrwServiceGroupIdsPluginType: FilterConditionPluginSpecification<
  AysNrwServiceGroupId[],
  FilterConditionNrwServiceGroupIdsDto,
  BdNrwServiceGroupCondition
> = {
  type: 'nrwServiceGroups',
  category: 'case',
  displayName: { de: 'NRW Leistungsgruppen' },
  sortingIndex: 7,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionNrwServiceGroupIdsDto,
  ): FilterConditionPluginImpl<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceGroupIdsPlugin(
        obj.nrwServiceGroupIds as AysNrwServiceGroupId[],
      );
  },
  fromCondition(
    jsonObj: BdNrwServiceGroupCondition,
  ): FilterConditionPluginImpl<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceGroupIdsPlugin(
        jsonObj.includes as AysNrwServiceGroupId[],
      );
  },
  newInstance(): FilterConditionPluginImpl<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceGroupIdsPlugin([]);
  },
};

export class FilterConditionNrwServiceGroupIdsPlugin extends FilterConditionPluginImpl<
  AysNrwServiceGroupId[],
  FilterConditionNrwServiceGroupIdsDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    AysNrwServiceGroupId[],
    FilterConditionNrwServiceGroupIdsDto,
    BdNrwServiceGroupCondition
  > = FilterConditionNrwServiceGroupIdsPluginType;

  private nrwServiceGroupIds: AysNrwServiceGroupId[];

  constructor(nrwServiceGroupIds: AysNrwServiceGroupId[]) {
    super(FilterConditionNrwServiceGroupIdsPluginType.type);
    // JSON round-trips can re-type integer-shaped ids ("1") as numbers, which
    // then break String#includes calls downstream. Normalise on the way in.
    this.nrwServiceGroupIds = (nrwServiceGroupIds ?? []).map(
      (id) => String(id) as AysNrwServiceGroupId,
    );
  }

  clone(): FilterConditionPlugin<AysNrwServiceGroupId[]> {
    return new FilterConditionNrwServiceGroupIdsPlugin([
      ...this.nrwServiceGroupIds,
    ]);
  }

  override equals(other: FilterConditionNrwServiceGroupIdsPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.nrwServiceGroupIds, other.nrwServiceGroupIds)
    );
  }

  override validationToText(): string {
    if (this.nrwServiceGroupIds.length === 0) {
      return 'Mindestens eine NRW Leistungsgruppe auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdNrwServiceGroupCondition(this.nrwServiceGroupIds, []);
  }

  toJSON(): FilterConditionNrwServiceGroupIdsDto | null {
    return {
      type: this.type,
      nrwServiceGroupIds: this.nrwServiceGroupIds,
    };
  }

  toText(): string | null {
    return this.nrwServiceGroupIds.length === 1
      ? 'eine NRW Leistungsgruppe'
      : `${this.nrwServiceGroupIds.length} NRW Leistungsgruppen`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.nrwServiceGroupIds.map((id) => {
      const displayName = AYS_NRW_SERVICE_GROUPS_DISPLAY_NAMES[id];
      return displayName ? `${id} — ${displayName}` : id;
    });
    return { title, items };
  }

  toValue(): AysNrwServiceGroupId[] {
    return this.nrwServiceGroupIds;
  }
}

export class BdNrwServiceGroupCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[] = []) {
    super(BD_NRW_SERVICE_GROUP_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionNrwServiceGroupIdsDto {
  type: string;
  nrwServiceGroupIds: string[];
}
